
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, MapPin, CalendarClock, Award, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SITE } from '@/data/site';
import { trackPhoneClick, trackMessengerClick, trackCta } from '@/lib/analytics';

// Custom SVGs for Social Icons
const WhatsappIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transform -translate-x-0.5 translate-y-0.5">
    <path d="M2.067 11.535l5.225 1.789 1.66 5.43c.188.586.995.666 1.284.127l2.56-4.793 4.965 3.868c.504.393 1.233.143 1.393-.466l3.682-14.156c.168-.646-.464-1.22-1.08-.98L2.067 9.873c-.663.26-.693 1.183 0 1.662z"/>
  </svg>
);

const TiktokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.01.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const ViberIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M20.592 11.085c-.073-2.92-1.196-5.37-3.328-7.24C15.11 1.956 12.39 1.042 9.073 1.042c-2.454 0-4.66.6-6.49 1.764-1.95 1.242-3.13 3.018-3.344 5.01-.137 1.29-.026 2.585.344 3.824.4 1.353 1.08 2.54 1.96 3.535.438.497 1.026 1.05 1.71 1.62.155.132.246.335.242.545l-.103 3.636c-.015.545.474.965.998.847l3.87-1.132c.162-.05.344-.047.514 0 1.28.37 2.628.527 3.99.527 2.922 0 5.613-1.023 7.644-2.855 2.112-1.905 3.32-4.475 3.377-7.394.02-.924-.037-1.874-.173-2.885zM8.406 14.86c-1.173-.082-2.185-.68-2.67-1.637-.215-.43-.274-.847-.28-1.564-.006-.826.04-1.01.298-1.393.307-.456.637-.674 1.144-.757.34-.055.602.012.87.218.423.326.892 1.05 1.076 1.666.173.57.173.832.002 1.25l-.234.566c.264.45 1.164 1.132 1.614 1.22l.533-.21c.545-.224.815-.224 1.32-.014.538.225 1.185.733 1.458 1.147.228.347.28.618.232 1.245-.044.577-.282.955-.783 1.24-.51.29-1.22.33-1.84.11-1.29-.46-2.072-1.287-2.74-2.086zM13.43 9.4c-1.294-.22-2.31-.91-2.92-1.983-.178-.31-.24-.512-.26-.848h.61c.01.21.05.372.138.56.402.855 1.24 1.545 2.227 1.834.403.118.966.166 1.432.12v.622c-.407.034-.82.023-1.227-.306zm2.345-1.096c-.63-.996-1.69-1.74-2.85-2.003-.314-.07-.866-.098-1.2-.065v-.602c.492-.023 1.183.02 1.597.126 1.524.39 2.924 1.405 3.65 2.65.25.42.41 1.042.443 1.572h-.602c-.015-.41-.122-1-.444-1.488h-.594zm2.146.402c-.417-1.425-1.334-2.65-2.585-3.45-1.26-.807-2.656-1.127-4.11-1.026v-.606c1.644-.127 3.324.237 4.793 1.165 1.48.93 2.545 2.378 3.012 4.045.18.63.264 1.48.225 2.14h-.603c.03-.53-.042-1.294-.225-1.854h-.507z"/>
  </svg>
);

const socialLinks = [
  { name: 'Viber', icon: ViberIcon, color: '#7B3FF2', href: SITE.viber },
  { name: 'WhatsApp', icon: WhatsappIcon, color: '#25D366', href: SITE.whatsapp },
  { name: 'Telegram', icon: TelegramIcon, color: '#0088cc', href: SITE.telegram },
  { name: 'TikTok', icon: TiktokIcon, color: '#000000', href: SITE.tiktok },
];

const advantages = [
  { id: 1, title: 'Гарантія якості', icon: ShieldCheck },
  { id: 2, title: 'Виїзд по Києву та області', icon: MapPin },
  { id: 3, title: 'Швидкий запис', icon: CalendarClock },
  { id: 4, title: 'Понад 10 років досвіду', icon: Award }
];

export default function HeroSection() {
  return (
    <section className="relative min-h-[100dvh] w-full flex flex-col justify-between pt-32 pb-16 overflow-hidden bg-[#1a1a1a]">
      
      {/* 1. HERO BACKGROUND */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center w-full h-full"
          style={{ backgroundImage: `url('${SITE.heroImage}')` }}
          aria-label="Наші роботи — заміна автоскла"
        />
        {/* Requested strict dark overlay */}
        <div 
          className="absolute inset-0" 
          style={{ backgroundColor: 'rgba(26, 26, 26, 0.45)' }} 
        />
        {/* Additional gradient for bottom fade blending if applicable */}
        <div className="absolute inset-0 gradient-overlay mix-blend-multiply opacity-80 pointer-events-none" />
      </div>

      <div className="relative z-10 flex flex-col justify-center flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 mb-16">
        
        {/* 2. MAIN CONTENT */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tight text-balance drop-shadow-lg"
            style={{ letterSpacing: '-0.02em' }}
          >
            Професійний сервіс автоскла в Києві
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            className="text-lg sm:text-xl md:text-2xl text-white max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-md"
          >
            Заміна лобового скла та тонування вікон для всіх марок автомобілів
          </motion.p>
        </div>

        {/* 3. ACTION BUTTONS */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12"
        >
          <Button 
            size="lg" 
            className="bg-[#003D7A] text-white border-0 text-lg px-10 h-16 rounded-xl w-full sm:w-auto min-w-[220px] transition-all duration-300 hover:scale-105 glow-effect" 
            asChild
          >
            <Link to="/contacts" onClick={() => trackCta("hero_get_price")}>
              Отримати вартість
            </Link>
          </Button>
          
          <Button 
            size="lg" 
            variant="outline" 
            className="bg-transparent border-2 border-white/80 text-white text-lg px-10 h-16 rounded-xl w-full sm:w-auto min-w-[220px] transition-all duration-300 hover:scale-105 hover:bg-white/10 hover:border-white backdrop-blur-sm" 
            asChild
          >
            <a
              href={`tel:${SITE.phoneE164Primary}`}
              onClick={() => trackPhoneClick(SITE.phoneE164Primary)}
            >
              <Phone className="w-5 h-5 mr-2" />
              {SITE.phonePrimary}
            </a>
          </Button>
        </motion.div>

        {/* 4. SOCIAL ICONS BLOCK */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          className="flex flex-wrap items-center justify-center gap-4"
        >
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-14 h-14 rounded-full text-white transition-all duration-300 hover:scale-110 hover:-translate-y-1 relative group overflow-hidden"
              style={{ 
                background: social.gradient || social.color,
                boxShadow: `0 4px 15px rgba(0,0,0,0.3)`
              }}
              aria-label={social.name}
              onClick={() => {
                const channel = social.name.toLowerCase();
                if (channel === 'whatsapp' || channel === 'telegram' || channel === 'viber') {
                  trackMessengerClick(channel);
                }
              }}
            >
              {/* Glow effect on hover matching the icon color */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ boxShadow: `0 0 20px ${social.color}` }}
              />
              <social.icon className="w-6 h-6 relative z-10" />
            </a>
          ))}
        </motion.div>

      </div>

      {/* 5. ADVANTAGES BLOCK */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {advantages.map((adv, index) => (
            <motion.div
              key={adv.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 + (index * 0.1), ease: "easeOut" }}
              className="glass-effect rounded-2xl p-6 flex items-center gap-5 transition-all duration-300 group hover:-translate-y-1 hover:border-[#003D7A]/50 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#003D7A]/0 via-[#003D7A]/5 to-[#003D7A]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%]" />
              
              <div className="w-14 h-14 rounded-full bg-[#003D7A]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#003D7A] transition-colors duration-300 group-hover:shadow-[0_0_15px_rgba(0,61,122,0.6)] border border-[#003D7A]/10">
                <adv.icon className="w-7 h-7 text-[#003D7A] group-hover:text-white transition-colors duration-300" />
              </div>
              
              <h3 className="text-lg sm:text-xl font-semibold text-white tracking-wide group-hover:text-blue-100 transition-colors duration-300 drop-shadow-sm">
                {adv.title}
              </h3>
            </motion.div>
          ))}
        </div>
      </div>
      
    </section>
  );
}
